/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Lookalike audiences", description = "Find users who resemble your best-performing audience segments using Hightouch's native lookalike modeling.", lastUpdatedDate = new Date(1788480000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Lookalike audiences",
        description: "Find users who resemble your best-performing audience segments using Hightouch's native lookalike modeling.",
        lastUpdatedDate: "Fri Sep 04 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "How it works",
          "depth": 1,
          "id": "how-it-works"
        }, {
          "value": "Use cases",
          "depth": 1,
          "id": "use-cases"
        }, {
          "value": "Create a lookalike audience trait",
          "depth": 0,
          "id": "create-a-lookalike-audience-trait"
        }, {
          "value": "Analyze lookalike results",
          "depth": 0,
          "id": "analyze-lookalike-results"
        }, {
          "value": "Percentile buckets",
          "depth": 1,
          "id": "percentile-buckets"
        }, {
          "value": "Similarity score chart",
          "depth": 1,
          "id": "similarity-score-chart"
        }, {
          "value": "AUC",
          "depth": 1,
          "id": "auc"
        }, {
          "value": "Evaluation metrics",
          "depth": 1,
          "id": "evaluation-metrics"
        }, {
          "value": "Use lookalike traits in audiences",
          "depth": 0,
          "id": "use-lookalike-traits-in-audiences"
        }, {
          "value": "Native vs. ad-platform lookalikes",
          "depth": 0,
          "id": "native-vs-ad-platform-lookalikes"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      code: "code",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      h3: "h3",
      ol: "ol",
      li: "li",
      ul: "ul",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and data teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A defined ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "schema"
                  }), " with at least one parent model (for example, ", _jsx(_components.code, {
                    children: "Users"
                  }), ")."]
                }), _jsxs("li", {
                  children: [_jsx(_components.a, {
                    href: "/customer-studio/predictive-traits#configure-predictive-inputs",
                    children: "Predictive inputs"
                  }), " configured in your schema (user properties and key events)."]
                }), _jsx("li", {
                  children: "An existing audience to use as your source (seed) audience."
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Lookalike audiences let you find users who are similar to a high-value segment — without requiring a specific conversion event or time window — so you can expand your reach to the people most likely to engage."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Predictive traits like purchase propensity require you to define a specific outcome event and prediction window. ", _jsx(_components.strong, {
          children: "Lookalike audiences"
        }), " take a different approach: instead of predicting a future action, they measure how similar each user is to the users in a source audience you define."]
      }), "\n", _jsx(_components.p, {
        children: "This is useful when you want to expand a proven segment — such as your highest-LTV customers, most engaged subscribers, or users who completed a multi-step onboarding — without needing to identify the exact event that makes them valuable."
      }), "\n", _jsxs(_components.p, {
        children: ["Lookalike audiences are part of the ", _jsx(_components.a, {
          href: "/customer-studio/predictive-traits",
          children: "predictive traits"
        }), " suite. They reuse the same predictive inputs (user properties and key events) and are exposed as traits in the audience builder."]
      }), "\n", _jsx(_components.h3, {
        id: "how-it-works",
        children: "How it works"
      }), "\n", _jsx(_components.p, {
        children: "When you create a lookalike audience trait, Hightouch:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Analyzes the users in your ", _jsx(_components.strong, {
            children: "source audience"
          }), " to build a profile of their shared characteristics using your configured predictive inputs."]
        }), "\n", _jsxs(_components.li, {
          children: ["Scores every user in the ", _jsx(_components.strong, {
            children: "match pool"
          }), " (all users in your parent model, or a filtered subset) based on how closely they resemble that profile."]
        }), "\n", _jsxs(_components.li, {
          children: ["Returns a ", _jsx(_components.strong, {
            children: "similarity percentile"
          }), " for each user, where higher percentiles indicate greater similarity to the source audience."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The result is a trait you can use like any other — filter on it in audience definitions, sync it to destinations, or combine it with other conditions."
      }), "\n", _jsx(_components.h3, {
        id: "use-cases",
        children: "Use cases"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Expand proven segments"
          }), " — Create a lookalike trait using your top-LTV audience as the source audience, filter for the top 10% similarity percentile in a new audience, and sync that audience to ad platforms for prospecting campaigns that mirror your best customers."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Prospecting without conversion data"
          }), " — Use a well-defined audience (for example, \"engaged enterprise users\") as the source audience for a lookalike trait, then build an audience of similar users to target with outreach — no single conversion event required."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Cross-sell targeting"
          }), " — Set buyers of one product line as the source audience, create a lookalike trait, and build an audience of high-similarity users who haven't purchased that line yet to sync to an ESP for cross-sell email campaigns."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Re-engagement"
          }), " — Create a lookalike trait based on your most active users, then build an audience of high-similarity but low-activity users and sync them to a push notification or email destination for re-engagement."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "create-a-lookalike-audience-trait",
        children: "Create a lookalike audience trait"
      }), "\n", _jsx(_components.p, {
        children: "To create a lookalike audience trait:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Traits"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create > New trait"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Method"
          }), " step, select a ", _jsx(_components.strong, {
            children: "parent model"
          }), " (for example, ", _jsx(_components.code, {
            children: "Users"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Calculation method"
          }), ", select ", _jsx(_components.strong, {
            children: "Lookalike Audience"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/lookalike-audiences/new-trait-lookalike.png",
          alt: "Method step with Lookalike Audience selected"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "6",
        children: ["\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Calculation"
          }), " step, select the audience you want to mimic. This is the ", _jsx(_components.strong, {
            children: "source audience"
          }), " — the group of users the model will use as the reference profile. Choose a well-defined audience that represents the type of user you want to find more of."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) Add filters to restrict the ", _jsx(_components.strong, {
            children: "match pool"
          }), " to specific users. For example, you might filter to users where ", _jsx(_components.code, {
            children: "device = desktop"
          }), " or limit scoring to a specific region."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set the ", _jsx(_components.strong, {
            children: "update schedule"
          }), " to control how often the model retrains and refreshes similarity scores."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/lookalike-audiences/calculate-trait-lookalike.png",
          alt: "Calculation step showing source audience selection, match pool filters, and update schedule"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "9",
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue to finalize"
          }), ". In the ", _jsx(_components.strong, {
            children: "Finalize"
          }), " step, name your trait, optionally add a description, review the calculation summary, and click ", _jsx(_components.strong, {
            children: "Create trait"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/lookalike-audiences/finalize-trait-lookalike.png",
          alt: "Finalize step showing trait name, description, and calculation summary with source audience and match pool"
        })
      }), "\n", _jsx(_components.p, {
        children: "The model trains using your configured predictive inputs and generates a similarity percentile for each user. Training time varies based on the size of your data."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Choose a source audience that is well-defined and large enough for the model\nto learn meaningful patterns. Very small source audiences (under a few hundred\nusers) may produce less reliable similarity scores."
        })
      }), "\n", _jsx(_components.h2, {
        id: "analyze-lookalike-results",
        children: "Analyze lookalike results"
      }), "\n", _jsxs(_components.p, {
        children: ["After the model finishes training, the ", _jsx(_components.strong, {
          children: "Prediction analysis"
        }), " tab on the trait detail page shows how well the model distinguishes source audience users from the rest of the match pool."]
      }), "\n", _jsx(_components.p, {
        children: "The analysis page displays the total number of users scored, the source audience name and size, and the last run date. Below these stats, you can explore results at different similarity thresholds."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/lookalike-audiences/lal-chart.png",
          alt: "Lookalike prediction analysis tab showing the source audience, percentile buckets, similarity score chart, AUC, and evaluation metrics"
        })
      }), "\n", _jsx(_components.h3, {
        id: "percentile-buckets",
        children: "Percentile buckets"
      }), "\n", _jsx(_components.p, {
        children: "The analysis page shows preset similarity buckets — Top 10%, Top 20%, and Top 50% — along with a Custom option. Each bucket displays the number of users in that tier and how much more similar they are to the source audience compared to the average user. Use these to gauge how concentrated similarity is at the top of the distribution."
      }), "\n", _jsx(_components.h3, {
        id: "similarity-score-chart",
        children: "Similarity score chart"
      }), "\n", _jsx(_components.p, {
        children: "A chart plots each user's similarity score against their percentile ranking. You can adjust the percentile range to focus on a specific slice of users — for example, the 80th to 100th percentile — to understand how scores are distributed among your highest-similarity users."
      }), "\n", _jsx(_components.h3, {
        id: "auc",
        children: "AUC"
      }), "\n", _jsx(_components.p, {
        children: "AUC (Area Under the Curve) measures how well the model separates source audience users from the match pool. It is computed on a held-out test split. A score closer to 100% indicates stronger separation."
      }), "\n", _jsx(_components.h3, {
        id: "evaluation-metrics",
        children: "Evaluation metrics"
      }), "\n", _jsx(_components.p, {
        children: "The evaluation metrics table shows Precision, Recall, and Lift at multiple cutoff thresholds (Top 0.1%, Top 1%, Top 5%, Top 10%, Top 25%). These metrics are computed on a held-out test split where a sample of the source audience is mixed into the match pool."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Precision"
          }), " — Of the top N% most similar users, the fraction that are in the heldout source audience."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Recall"
          }), " — Of all heldout source audience users, the fraction that appear in the top N% most similar users."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lift"
          }), " — How much more likely the top N% of users are to be in the heldout source audience compared to random selection."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "use-lookalike-traits-in-audiences",
        children: "Use lookalike traits in audiences"
      }), "\n", _jsx(_components.p, {
        children: "After the model finishes training, the lookalike trait appears in the Traits list and can be used like any other trait."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Audiences"
          }), " and create a new audience."]
        }), "\n", _jsx(_components.li, {
          children: "Add a filter using your lookalike audience trait."
        }), "\n", _jsxs(_components.li, {
          children: ["Set a ", _jsx(_components.strong, {
            children: "percentile threshold"
          }), " to control how similar users must be to qualify. For example, filtering for the top 10% returns users who most closely resemble your source audience."]
        }), "\n", _jsx(_components.li, {
          children: "(Optional) Combine the lookalike filter with other conditions — demographics, behavioral traits, or other predictive traits — to further refine the audience."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/lookalike-audiences/lal-trait-audience-filter.png",
          alt: "Audience builder with a lookalike trait filter showing percentile range"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once defined, you can sync the audience to any connected destination for activation: ad platforms, ESPs, CRMs, or any other tool in your stack."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Lookalike similarity scores update on a schedule. Between updates, new users\nor behavior changes won't be reflected until the next model run."
        })
      }), "\n", _jsx(_components.h2, {
        id: "native-vs-ad-platform-lookalikes",
        children: "Native vs. ad-platform lookalikes"
      }), "\n", _jsx(_components.p, {
        children: "Many ad platforms (Meta, Google, and others) offer their own lookalike or similar-audience features. Hightouch's native lookalike audiences complement these by giving you more control:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Native lookalikes (Hightouch)"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Ad-platform lookalikes"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data used"
              })
            }), _jsx(_components.td, {
              children: "All user properties and behavioral events in your warehouse"
            }), _jsx(_components.td, {
              children: "Only data the platform has collected"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Where usable"
              })
            }), _jsx(_components.td, {
              children: "Any destination — ads, email, CRM, push, and more"
            }), _jsx(_components.td, {
              children: "Only within that ad platform"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Transparency"
              })
            }), _jsx(_components.td, {
              children: "You control the source audience, see similarity scores, and can combine with other filters"
            }), _jsx(_components.td, {
              children: "Platform controls the algorithm; limited visibility into how similarity is determined"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Cross-channel"
              })
            }), _jsx(_components.td, {
              children: "Same lookalike audience can be synced everywhere"
            }), _jsx(_components.td, {
              children: "Must be rebuilt per platform"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "You can use both approaches together: build a refined seed audience in Hightouch using native lookalike scores, then sync that seed to an ad platform to create a platform-native lookalike on top."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "Build an audience"
          }), " to filter on a similarity percentile in the visual builder."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/syncs",
            children: "Sync audiences"
          }), " to send a high-similarity audience to ad platforms and other destinations."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/predictive-traits",
            children: "Predictive traits"
          }), " to score the likelihood of a specific future action instead of overall similarity."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/catalog-recommendations",
            children: "Catalog recommendations"
          }), " to rank the top items from a catalog for each user."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
